import React, { useEffect, useCallback, useState, useMemo } from 'react';
import Sidebar from '../components/shared/Sidebar';
import DataGrid, { GridColumn } from '../components/data/DataGrid';
import FinancialRecordFilterPanel from '../components/data/FinancialRecordFilterPanel';
import financialRecordKPIService from '../services/financialRecordKPIService';
import { FinancialRecord, FinancialRecordFilter, KPI } from '../types/data/financialRecord';
import AIAnalysisButton from '../components/csv/AIAnalysisButton';
import ReportPopup from '../components/csv/ReportPopup';
import ValidationRuleService from '../services/validationRuleService';
import useAuthStore from '../store/authStore';
import { ValidationRule } from '../types/ValidationRule';
import { User } from '../types/user';
import { UserService } from '../services/userService';
import { getKPIName } from '../utils/kpiMapping';
import './KPIAnalyticsPage.css';

// Add a default placeholder for the prompt input field
const DEFAULT_PROMPT_PLACEHOLDER = "Ask a question about the KPI data. For example: 'Compare the top 5 dealers by profit and show their performance trends' or 'Analyze the relationship between sales revenue and gross profit with visualizations'";

// Claude 4.5 Sonnet ARN model ID (primary model)
const CLAUDE_45_SONNET_MODEL_ID = 'arn:aws:bedrock:eu-central-1:586794487615:inference-profile/eu.anthropic.claude-sonnet-4-5-20250929-v1:0';

// Claude 3.7 Sonnet as fallback when 4.0 gets throttled
const CLAUDE_37_SONNET_MODEL_ID = 'arn:aws:bedrock:eu-central-1::inference-profile/eu.anthropic.claude-3-7-sonnet-20250219-v1:0';

// Mock analysis result for testing PDF export
const MOCK_ANALYSIS_RESULT = `# KPI Analysis Report

## Executive Summary

Based on the comprehensive analysis of the KPI data, here are the key findings:

### Key Performance Indicators
- **Total Revenue**: $2.4M across all dealerships
- **Average Gross Profit Margin**: 18.5%
- **Top Performing Region**: North East (22% above average)
- **Service Department Efficiency**: 94% utilization rate

### Performance Highlights

#### Top 3 Performing Dealerships
1. **Premium Motors North** - $485K revenue, 24% profit margin
2. **City Auto Center** - $421K revenue, 21% profit margin  
3. **Highway Dealership** - $398K revenue, 19% profit margin

#### Areas for Improvement
- **Parts Department**: 12% below target efficiency
- **Customer Satisfaction**: Service wait times averaging 45 minutes
- **Inventory Turnover**: 15% slower than industry benchmark

### Recommendations

1. **Optimize Parts Inventory**: Implement just-in-time ordering system
2. **Enhance Service Capacity**: Add 2 additional service bays during peak hours
3. **Staff Training Program**: Focus on customer service excellence
4. **Digital Marketing**: Increase online presence for underperforming locations

### Financial Projections

The dealership group is performing well overall, with particularly strong results in service operations and new vehicle sales. By addressing the specific recommendations outlined above, there's potential to increase overall profitability by an estimated 12-15% in the next fiscal year.

I'll be happy to provide more detailed analysis on any specific area that requires further investigation.`;

// Prompt suggestions for different analyses
const QUESTION_SUGGESTIONS = [
  {
    title: "KPI Compliance Analysis",
    question: "Can you identify and highlight any KPI data points that fall outside of standard performance norms and best practices?"
  },
  {
    title: "Top Performers Analysis",
    question: "What are the top 3 performing entities based on key performance indicators?"
  },
  {
    title: "KPI Trend Analysis",
    question: "Which KPI categories show the most significant changes over the last 6 months?"
  },
  {
    title: "Department Performance Analysis",
    question: "Compare the KPI performance across different departments or business units. Which areas show the strongest results?"
  }
];

// Custom prompt for the questions feature
const QUESTIONS_PROMPT = 
  "You are an expert KPI analyst. " +
  "Please analyze the provided KPI data and answer the following specific question from the user. " +
  "Present your analysis with clear markdown headings and keep explanations concise. " +
  
  "VISUALIZATION FORMAT: " +
  "When presenting visualizations, use the following Chart.js format: " +
  
  "SUPPORTED CHART TYPES: bar, line, area, pie, doughnut, scatter, bubble, radar, polarArea, histogram\n\n" +
  
  "For basic charts (bar, line, area, pie, doughnut, radar, polarArea):\n" +
  "```visualization\n" +
  "{\n" +
  "  \"type\": \"bar\",\n" +
  "  \"title\": \"Example Chart Title\",\n" +
  "  \"description\": \"Brief description\",\n" +
  "  \"labels\": [\"Label 1\", \"Label 2\", \"Label 3\"],\n" +
  "  \"datasets\": [{ \"label\": \"Dataset Name\", \"data\": [10, 20, 30] }],\n" +
  "  \"xAxisLabel\": \"X-Axis\",\n" +
  "  \"yAxisLabel\": \"Y-Axis\"\n" +
  "}\n" +
  "```\n\n" +
  
  "For scatter plots:\n" +
  "```visualization\n" +
  "{\n" +
  "  \"type\": \"scatter\",\n" +
  "  \"title\": \"Scatter Plot Title\",\n" +
  "  \"description\": \"Brief description\",\n" +
  "  \"labels\": [\"Point 1\", \"Point 2\", \"Point 3\"],\n" +
  "  \"datasets\": [{ \"label\": \"Dataset Name\", \"data\": [{\"x\": 10, \"y\": 20}, {\"x\": 15, \"y\": 25}] }],\n" +
  "  \"xAxisLabel\": \"X-Axis\",\n" +
  "  \"yAxisLabel\": \"Y-Axis\"\n" +
  "}\n" +
  "```\n\n" +
  
  "For bubble charts:\n" +
  "```visualization\n" +
  "{\n" +
  "  \"type\": \"bubble\",\n" +
  "  \"title\": \"Bubble Chart Title\",\n" +
  "  \"description\": \"Brief description\",\n" +
  "  \"labels\": [\"Bubble 1\", \"Bubble 2\", \"Bubble 3\"],\n" +
  "  \"datasets\": [{ \"label\": \"Dataset Name\", \"data\": [{\"x\": 10, \"y\": 20, \"r\": 5}, {\"x\": 15, \"y\": 25, \"r\": 8}] }],\n" +
  "  \"xAxisLabel\": \"X-Axis\",\n" +
  "  \"yAxisLabel\": \"Y-Axis\"\n" +
  "}\n" +
  "```\n\n" +
  
  "For histograms:\n" +
  "```visualization\n" +
  "{\n" +
  "  \"type\": \"histogram\",\n" +
  "  \"title\": \"Histogram Title\",\n" +
  "  \"description\": \"Brief description\",\n" +
  "  \"labels\": [],\n" +
  "  \"datasets\": [{ \"label\": \"Frequency\", \"data\": [1, 5, 10, 15, 20, 18, 12, 8, 3, 1] }],\n" +
  "  \"bins\": 10,\n" +
  "  \"xAxisLabel\": \"Value Ranges\",\n" +
  "  \"yAxisLabel\": \"Frequency\"\n" +
  "}\n" +
  "```\n\n" +
  
  "SPECIAL HISTOGRAM EXAMPLE - ENSURE COMPLETE JSON:\n" +
  "```visualization\n" +
  "{\n" +
  "  \"type\": \"histogram\",\n" +
  "  \"title\": \"Distribution of Values\",\n" +
  "  \"description\": \"Frequency distribution of raw data points\",\n" +
  "  \"labels\": [],\n" +
  "  \"datasets\": [{ \"label\": \"Frequency\", \"data\": [10.5, 12.3, 15.7, 18.2, 20.1, 22.8, 25.3, 27.9, 30.4] }],\n" +
  "  \"bins\": 8,\n" +
  "  \"xAxisLabel\": \"Value\",\n" +
  "  \"yAxisLabel\": \"Count\"\n" +
  "}\n" +
  "```\n\n" +
  
  "IMPORTANT: \n" +
  "- For scatter and bubble plots, always provide a labels array with meaningful names for each data point\n" +
  "- For bubble charts, use x, y, r format where r is the bubble radius\n" +
  "- For histograms, provide raw data and optionally specify bins count\n" +
  "- Use 'area' type for filled line charts, 'polarArea' for polar area charts\n\n" +
  "CRITICAL JSON FORMAT REQUIREMENTS:\n" +
  "- ALL JSON objects MUST be complete with matching brackets and braces\n" +
  "- Always include closing brackets ']' for arrays and closing braces '}' for objects\n" +
  "- For histograms, always include the 'labels' property (can be empty array: [])\n" +
  "- Double-check that all JSON is valid and properly formatted before returning\n\n";

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Custom AI Analysis Button for KPI Analytics page
interface CustomAIAnalysisButtonProps {
  prompt?: string;
  modelId?: string;
  disabled?: boolean;
  createCSVContent: () => Promise<string>;
  onAnalysisComplete: (result: string) => void;
  onOpenReport: () => void;
}

const CustomAIAnalysisButton: React.FC<CustomAIAnalysisButtonProps> = ({ 
  prompt, 
  modelId, 
  disabled,
  createCSVContent,
  onAnalysisComplete,
  onOpenReport
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [usedFallback, setUsedFallback] = useState(false);
  const [analysisFileRef, setAnalysisFileRef] = useState<string | null>(null);

  // Function to save filtered data as a file
  const saveFilteredDataAsFile = async (content: string): Promise<string> => {
    try {
      const response = await fetch(`${API_BASE_URL}/bedrock/save-filtered-data`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server response:', errorData);
        throw new Error(errorData.error || 'Failed to save filtered data');
      }

      const data = await response.json();
      return data.fileRef;
    } catch (err: any) {
      console.error('Error saving filtered data:', err);
      throw new Error('Failed to save filtered data for analysis');
    }
  };

  const handleAnalyze = async () => {
    if (!prompt || disabled) return;

    setIsAnalyzing(true);
    setError(null);
    setUsedFallback(false);
    setAnalysisResult(null);
    setAnalysisFileRef(null);

    try {
      // Create CSV content from current filtered data
      const csvContent = await createCSVContent();
      if (!csvContent) {
        throw new Error('No data available for analysis');
      }

      // Save the filtered data as a file
      const fileRef = await saveFilteredDataAsFile(csvContent);
      setAnalysisFileRef(fileRef);

      // Try primary model first, then fallback
      const makeAnalysisRequest = async (useModelId: string) => {
        const response = await fetch(`${API_BASE_URL}/bedrock/analyze/file`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            prompt,
            modelId: useModelId,
            fileRef: fileRef,
            requestType: 'submission_validation_rules', // Specific request type for validation rules analysis
            maxTokenLimit: useModelId === CLAUDE_37_SONNET_MODEL_ID ? 200000 : undefined // Use token limit for 3.7 fallback
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Analysis failed with status ${response.status}`);
        }

        return await response.json();
      };

      try {
        // Try Claude 4.0 Sonnet first
        const result = await makeAnalysisRequest(modelId || CLAUDE_45_SONNET_MODEL_ID);
        setAnalysisResult(result.result);
        onAnalysisComplete(result.result);
        onOpenReport(); // Automatically open the report popup
      } catch (primaryError: any) {
        // Check if it's a throttling error
        const isThrottled = primaryError.message?.includes('throttled') || 
                           primaryError.message?.includes('ServiceUnavailableException') ||
                           primaryError.message?.includes('TooManyRequestsException');
        
        if (isThrottled) {
          console.warn('Claude 4.0 Sonnet is throttled, falling back to Claude 3.7 Sonnet:', primaryError.message);
          
          try {
            // Fallback to Claude 3.7 Sonnet when 4.0 is throttled
            const result = await makeAnalysisRequest(CLAUDE_37_SONNET_MODEL_ID);
            setAnalysisResult(result.result);
            setUsedFallback(true);
            onAnalysisComplete(result.result);
            onOpenReport(); // Automatically open the report popup
          } catch (fallbackError) {
            console.error('Both models failed:', fallbackError);
            throw fallbackError;
          }
        } else {
          // If it's not a throttling error, don't use fallback
          console.error('Claude 3.7 Sonnet failed with non-throttling error:', primaryError);
          throw primaryError;
        }
      }

    } catch (err: any) {
      console.error('Analysis error:', err);
      
      // Handle specific error types with user-friendly messages
      let errorMessage = err.message || 'An error occurred during analysis';
      
      if (err.message?.includes('Input is too long for requested model')) {
        errorMessage = '📊 **Dataset Too Large for Validation**\n\nYour dataset is too large for the AI model to process the validation rules analysis. Please try:\n\n• **Reduce Time Range**: Select fewer months or years\n• **Filter Locations**: Choose specific dealerships\n• **Select Fewer Rules**: Uncheck some validation rules to focus the analysis\n• **Smaller Data Subset**: Use the filter options to narrow your dataset\n\nOnce filtered, try running the validation analysis again.';
      } else if (err.message?.includes('ValidationException')) {
        errorMessage = '⚠️ **Validation Analysis Error**\n\nThere was an issue processing your validation rules with the AI model.\n\nPlease try:\n• Selecting fewer validation rules\n• Filtering to a smaller dataset\n• Checking that your data format is correct';
      } else if (err.message?.includes('context limit') || err.message?.includes('token limit')) {
        errorMessage = '📈 **Too Much Data for Analysis**\n\nThe selected data and validation rules exceed what the AI can process.\n\n**Solutions:**\n• Reduce the number of selected validation rules\n• Filter your data to focus on specific periods or locations\n• Try analyzing smaller batches of data\n\nThis will provide more focused validation insights!';
      }
      
      setError(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="custom-ai-analysis-container">
      <button
        onClick={handleAnalyze}
        disabled={disabled || isAnalyzing || !prompt}
        className={`primary-button ${isAnalyzing ? 'analyzing' : ''}`}
      >
        {isAnalyzing ? (
          <>
            <div className="spinner-small"></div>
            Running validation rules...
          </>
        ) : (
          'Run validation rules'
        )}
      </button>

      {error && (
        <div className="analysis-error">
          <strong>Analysis Error:</strong> {error}
        </div>
      )}

      {usedFallback && (
        <div className="fallback-notice">
          <strong>Note:</strong> Using fallback model (Claude 3.5 Sonnet)
        </div>
      )}

      {analysisResult && (
        <div className="analysis-preview">
          <div className="analysis-preview-content">
            <h4>KPI Analysis Complete</h4>
            <p>Your KPI analysis is ready. Click below to view the full report with visualizations.</p>
          </div>
          <button onClick={onOpenReport} className="view-report-button">
            View Full KPI Report
          </button>
        </div>
      )}
    </div>
  );
};

// KPI Details Component for expanded rows
const KPIDetailsComponent: React.FC<{ record: FinancialRecord; appliedFilter?: FinancialRecordFilter }> = ({ record, appliedFilter }) => {
  // Filter KPIs based on applied filters
  const filteredKpis = record.kpis?.filter((kpi: KPI) => {
    // Filter by KPI codes if specified (from validation rules)
    if (appliedFilter?.kpiCodes && appliedFilter.kpiCodes.length > 0) {
      if (!appliedFilter.kpiCodes.includes(kpi.code)) {
        return false;
      }
    }
    
    // Filter by KPI categories if specified
    if (appliedFilter?.kpiCategories && appliedFilter.kpiCategories.length > 0) {
      return kpi.category && appliedFilter.kpiCategories.includes(kpi.category);
    }
    
    return true; // No filter applied, show all KPIs
  }) || [];

  if (filteredKpis.length === 0) {
    return (
      <div className="kpi-details-empty">
        <p>No KPI data available for this record.</p>
      </div>
    );
  }

  return (
    <div className="kpi-details-container">
      <h4>KPI Details for {record.dealer} - {record.year}/{record.month}</h4>
      <div className="kpi-grid">
        {filteredKpis.map((kpi: KPI, index: number) => (
          <div key={index} className="kpi-detail-item">
            <div className="kpi-name">{getKPIName(kpi.code)}</div>
            <div className="kpi-category">{kpi.category}</div>
            <div className="kpi-value">{kpi.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const KPIAnalyticsPage: React.FC = () => {
  const { user } = useAuthStore();
  
  // State management
  const [records, setRecords] = useState<FinancialRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FinancialRecordFilter>({});
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string | number>>(new Set());
  const [allRecordsForFilters, setAllRecordsForFilters] = useState<FinancialRecord[]>([]);
  const [userMap, setUserMap] = useState<Map<string, User>>(new Map());
  const [filterUserIds, setFilterUserIds] = useState<string[]>([]);
  
  // Validation rules state
  const [selectedValidationRules, setSelectedValidationRules] = useState<ValidationRule[]>([]);
  const [userValidationRules, setUserValidationRules] = useState<ValidationRule[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [rulesError, setRulesError] = useState<string | null>(null);
  
  // Report state
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  
  // High accuracy mode state
  const [highAccuracyMode, setHighAccuracyMode] = useState(false);
  
  // KPI codes for highlighting
  const [currentKPICodes, setCurrentKPICodes] = useState<string[]>([]);
  
  const openReport = () => setIsReportOpen(true);
  const closeReport = () => setIsReportOpen(false);

  const handleDeleteRecord = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    try {
      await financialRecordKPIService.deleteFinancialRecord(id);
      // Refresh the records after deletion
      fetchRecords();
    } catch (err: any) {
      setError('Error deleting record. Please try again.');
      console.error('Error deleting record:', err);
    }
  };

  // Define columns for the data grid
  const columns: GridColumn[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'brand', headerName: 'Brand', width: 120 },
    { field: 'dealer', headerName: 'Dealer', width: 200 },
    { field: 'location_id', headerName: 'Location ID', width: 100, type: 'number' },
    { field: 'groups', headerName: 'Groups', width: 150, 
      valueFormatter: (value) => Array.isArray(value) ? value.join(', ') : value || '' },
    { field: 'year', headerName: 'Year', width: 100, type: 'number' },
    { field: 'month', headerName: 'Month', width: 100, type: 'number' },
    { field: 'kpiCount', headerName: 'KPI Count', width: 120, type: 'number' },
    { field: 'ownerName', headerName: 'Owner', width: 150 },
    { 
      field: 'updatedAt', 
      headerName: 'Last Updated', 
      width: 160, 
      type: 'date',
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleString();
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <div className="row-actions">
          <button
            onClick={() => handleDeleteRecord(params.row.id)}
            className="delete-button"
            title="Delete Record"
          >
            🗑️
          </button>
        </div>
      )
    }
  ];

  // Handle row expansion
  const handleRowExpand = (rowId: string | number) => {
    const newExpandedRowIds = new Set(expandedRowIds);
    newExpandedRowIds.add(rowId);
    setExpandedRowIds(newExpandedRowIds);
  };

  // Handle row collapse
  const handleRowCollapse = (rowId: string | number) => {
    const newExpandedRowIds = new Set(expandedRowIds);
    newExpandedRowIds.delete(rowId);
    setExpandedRowIds(newExpandedRowIds);
  };

  // Transform records for the data grid
  const transformedRecords = useMemo(() => {
    return records.map(record => {
      // Get owner name from userMap if available
      let ownerName = '';
      if (record.userId) {
        // Ensure consistent userId format for lookup
        const userId = String(parseInt(String(record.userId)));
        const user = userMap.get(userId);
        ownerName = user?.name || (record as any).ownerName || userId;
      }

      // Filter KPIs based on applied filters for accurate count
      const filteredKpis = record.kpis?.filter((kpi: KPI) => {
        // Get KPI codes from selected validation rules
        const selectedKpiCodes = new Set<string>();
        selectedValidationRules.forEach(rule => {
          if (rule.kpiCodes) {
            try {
              const kpiCodes = JSON.parse(rule.kpiCodes) as string[];
              kpiCodes.forEach(code => selectedKpiCodes.add(code));
            } catch (error) {
              // Ignore parsing errors
            }
          }
        });
        
        // Filter by KPI codes if validation rules are selected
        if (selectedKpiCodes.size > 0) {
          if (!selectedKpiCodes.has(kpi.code)) {
            return false;
          }
        }
        
        // Filter by KPI categories if specified
        if (filter.kpiCategories && filter.kpiCategories.length > 0) {
          return kpi.category && filter.kpiCategories.includes(kpi.category);
        }
        
        return true; // No filter applied, count all KPIs
      }) || [];

      return {
        ...record,
        kpiCount: filteredKpis.length,
        groups: record.groups || [],
        ownerName // Add owner name to transformed record
      };
    });
  }, [records, userMap, filter.kpiCategories, selectedValidationRules]);

  // Fetch records function
  const fetchRecords = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get KPI codes from selected validation rules for filtering
      const selectedKpiCodes = new Set<string>();
      selectedValidationRules.forEach(rule => {
        if (rule.kpiCodes) {
          try {
            const kpiCodes = JSON.parse(rule.kpiCodes) as string[];
            kpiCodes.forEach(code => selectedKpiCodes.add(code));
          } catch (error) {
            console.warn('Error parsing KPI codes for rule:', rule.id, error);
          }
        }
      });

      // Build the filter with KPI codes from validation rules
      const apiFilter = {
        ...filter,
        page: currentPage,
        pageSize: pageSize,
        owners: filter.owners // Make sure owners filter is included
      };

      // If validation rules are selected with KPI codes, filter by those KPIs
      if (selectedKpiCodes.size > 0) {
        apiFilter.kpiCodes = Array.from(selectedKpiCodes);
        console.log(`🎯 FILTERING: Main table filtered to ${selectedKpiCodes.size} KPIs from validation rules: ${Array.from(selectedKpiCodes).join(', ')}`);
      } else if (selectedValidationRules.length > 0) {
        // If validation rules are selected but no KPI codes found, log warning
        console.warn(`⚠️ WARNING: ${selectedValidationRules.length} validation rules selected but no KPI codes found for filtering`);
      } else {
        console.log(`📊 INFO: No validation rules selected - showing all KPIs (potentially 100+ KPIs)`);
      }

      const response = await financialRecordKPIService.getFinancialRecords(apiFilter);
      
            setRecords(response.records || []);
      setTotalCount(response.totalCount || 0);
      
                } catch (err: any) {
            setError(err.message || 'Failed to fetch records');
      setRecords([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Render expanded row content
  const renderExpandedRow = (row: any) => {
    const record = records.find(r => r.id === row.id);
    if (!record) return null;
    
    // Create a computed filter that includes KPI codes from validation rules
    const computedFilter = { ...filter };
    
    // Add KPI codes from selected validation rules to the filter
    const selectedKpiCodes = new Set<string>();
    selectedValidationRules.forEach(rule => {
      if (rule.kpiCodes) {
        try {
          const kpiCodes = JSON.parse(rule.kpiCodes) as string[];
          kpiCodes.forEach(code => selectedKpiCodes.add(code));
        } catch (error) {
          // Ignore parsing errors
        }
      }
    });
    
    // Only add KPI codes to filter if validation rules are selected
    if (selectedKpiCodes.size > 0) {
      computedFilter.kpiCodes = Array.from(selectedKpiCodes);
    }
    
    return <KPIDetailsComponent record={record} appliedFilter={computedFilter} />;
  };

  // Handle filter application
  const handleApplyFilter = useCallback((newFilter: FinancialRecordFilter) => {
        setFilter(newFilter);
    setCurrentPage(1); // Reset to first page when filtering
    // Clear expanded rows when filter changes
    setExpandedRowIds(new Set());
  }, []);

  // Handle filter clearing
  const handleClearFilter = useCallback(() => {
        setFilter({});
    setCurrentPage(1); // Reset to first page when clearing filter
    // Clear expanded rows when filter is cleared
    setExpandedRowIds(new Set());
  }, []);

  // Handle page changes
  const handlePageChange = useCallback((page: number) => {
    if (page >= 1 && page <= Math.ceil(totalCount / pageSize)) {
      setCurrentPage(page);
      // Clear expanded rows when page changes
      setExpandedRowIds(new Set());
    }
  }, [totalCount, pageSize]);

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / pageSize);

  // Validation rules functionality
  const fetchUserValidationRules = async () => {
    if (!user?.id) return;
    
    setIsLoadingRules(true);
    setRulesError(null);
    
    try {
      // Filter validation rules by currently selected brands
      const currentBrands = filter.brands && filter.brands.length > 0 ? filter.brands : undefined;
      const rules = await ValidationRuleService.getUserValidationRulesByBrands(currentBrands);
      setUserValidationRules(rules);
      // Select all rules by default
      setSelectedValidationRules(rules);
    } catch (error) {
      console.error('Error fetching validation rules:', error);
      setRulesError('Failed to load your validation rules');
    } finally {
      setIsLoadingRules(false);
    }
  };

  // Handle validation rule selection
  const handleRuleToggle = (rule: ValidationRule) => {
    setSelectedValidationRules(prev => {
      const isSelected = prev.some(r => r.id === rule.id);
      if (isSelected) {
        return prev.filter(r => r.id !== rule.id);
      } else {
        return [...prev, rule];
      }
    });
  };

  // Extract filter options from all available records based on current filter
  const updateFilterOptionsFromRecords = useCallback(async () => {
    try {
            // Fetch ALL records with current filter to get available filter options
      const response = await financialRecordKPIService.getFinancialRecords({
        ...filter, // Apply current filter to get relevant options
        page: 1,
        pageSize: 50000 // Large page size to get all records
      });
      
      const allRecords = response.records || [];
            // Store all records for filter panel to use
      setAllRecordsForFilters(allRecords);
      
      if (allRecords.length > 0) {
        // Extract all unique owner IDs from these records
        if (user?.role === 'admin' || user?.role === 'group_admin') {
          const uniqueOwnerIds = Array.from(new Set(
            allRecords
              .map(record => record.userId ? String(parseInt(String(record.userId))) : undefined)
              .filter((userId): userId is string => userId !== undefined && userId !== null)
          ));
          
                    // Fetch user details for these IDs
          if (uniqueOwnerIds.length > 0) {
                        const users = await UserService.getUsersByIds(uniqueOwnerIds);
                        // Create a map of user ID to user object using the same transformation
            const newUserMap = new Map<string, User>();
            users.forEach(u => {
              // Ensure consistent ID format
              const userId = String(parseInt(String(u.id)));
              newUserMap.set(userId, u);
                          });
            
            setUserMap(newUserMap);
            setFilterUserIds(uniqueOwnerIds);
            
                                  }
        }
      } else {
        // If no records found, set empty arrays
        setAllRecordsForFilters([]);
        setFilterUserIds([]);
        setUserMap(new Map());
      }
    } catch (error) {
      console.error('Error fetching records for filter options:', error);
    }
  }, [user?.role, filter]);

  // Fetch validation rules when user or brand filter changes
  useEffect(() => {
    fetchUserValidationRules();
  }, [user?.id, filter.brands]);

  // Fetch records when filter, page, or validation rules change (this handles initial load too)
  useEffect(() => {
    fetchRecords();
  }, [filter, currentPage, pageSize, selectedValidationRules]);
  
  // Update filter options when filter changes or component mounts
  useEffect(() => {
    if (user?.role) {
      updateFilterOptionsFromRecords();
    }
  }, [user?.role, updateFilterOptionsFromRecords]);

  // Create CSV content from current records for AI analysis
  const createCSVContent = async () => {
    try {
      // Fetch ALL records without pagination for AI analysis
      const allRecordsFilter: any = {
        page: 1,
        pageSize: 10000 // Large page size to get all records
      };
      
      // Get KPI codes from selected validation rules for pre-filtering
      const selectedKpiCodes = new Set<string>();
      selectedValidationRules.forEach(rule => {
        if (rule.kpiCodes) {
          try {
            const kpiCodes = JSON.parse(rule.kpiCodes) as string[];
            kpiCodes.forEach(code => selectedKpiCodes.add(code));
          } catch (error) {
            console.warn('Error parsing KPI codes for rule:', rule.id, error);
          }
        }
      });

      // Apply current filters but remove pagination limits
      if (filter.brands && filter.brands.length > 0) allRecordsFilter.brands = filter.brands;
      if (filter.dealers && filter.dealers.length > 0) allRecordsFilter.dealers = filter.dealers;
      if (filter.years && filter.years.length > 0) allRecordsFilter.years = filter.years;
      if (filter.months && filter.months.length > 0) allRecordsFilter.months = filter.months;
      if (filter.owners && filter.owners.length > 0) allRecordsFilter.owners = filter.owners;
      
      // Pre-filter by KPI codes from validation rules if available
      if (selectedKpiCodes.size > 0) {
        allRecordsFilter.kpiCodes = Array.from(selectedKpiCodes);
        console.log(`🎯 AI ANALYSIS FILTERING: Pre-filtering records to ${selectedKpiCodes.size} KPIs from validation rules: ${Array.from(selectedKpiCodes).join(', ')}`);
      } else if (filter.kpiCodes && filter.kpiCodes.length > 0) {
        allRecordsFilter.kpiCodes = filter.kpiCodes;
        console.log(`🎯 AI ANALYSIS FILTERING: Using manual KPI filter: ${filter.kpiCodes.join(', ')}`);
      } else {
        console.warn(`⚠️ AI ANALYSIS WARNING: No KPI filtering applied - will send ALL KPIs (potentially 100+) for analysis. This may be inefficient.`);
      }
      
      if (filter.kpiCategories && filter.kpiCategories.length > 0) allRecordsFilter.kpiCategories = filter.kpiCategories;
      
      console.log('Fetching ALL records for AI analysis with filter:', allRecordsFilter);
      
      const response = await financialRecordKPIService.getFinancialRecords(allRecordsFilter);
      const allRecords = response.records || [];
      
      console.log(`Fetched ${allRecords.length} records for AI analysis`);
      
      if (allRecords.length === 0) return '';
      
      // Create headers
      const headers = ['ID', 'Brand', 'Dealer', 'Groups', 'Year', 'Month', 'Owner', 'CreatedAt', 'UpdatedAt'];
      
      // Determine which KPIs to include in the CSV
      let kpiHeaders: string[];
      if (selectedKpiCodes.size === 0) {
        // If no validation rules are selected, we should still limit the KPIs to avoid sending 100+ KPIs
        // Get all available KPI codes but warn the user
        const allKpiCodes = new Set<string>();
        allRecords.forEach(record => {
          if (record.kpis) {
            record.kpis.forEach(kpi => allKpiCodes.add(kpi.code));
          }
        });
        kpiHeaders = Array.from(allKpiCodes).sort();
        console.warn(`⚠️ CSV GENERATION WARNING: No KPI filtering applied - including ALL ${kpiHeaders.length} available KPIs. This may result in inefficient analysis.`);
        
        // Optional: Limit to first 50 KPIs if too many (uncomment if needed)
        // if (kpiHeaders.length > 50) {
        //   console.warn(`🚨 LIMITING: Too many KPIs (${kpiHeaders.length}), limiting to first 50 for efficiency`);
        //   kpiHeaders = kpiHeaders.slice(0, 50);
        // }
      } else {
        // Only include KPIs that are referenced in the selected validation rules
        kpiHeaders = Array.from(selectedKpiCodes).sort();
        console.log(`✅ CSV GENERATION: Filtering CSV to only include ${kpiHeaders.length} KPIs from validation rules: ${kpiHeaders.join(', ')}`);
      }
      // Add KPI names as headers instead of codes for better AI understanding
      const kpiNameHeaders = kpiHeaders.map(code => getKPIName(code));
      headers.push(...kpiNameHeaders);
      
      // Store KPI codes for highlighting (do this after analysis to avoid affecting prompt)
      // setCurrentKPICodes(kpiHeaders);
      console.log('KPI codes available for highlighting:', kpiHeaders);
      
      // Collect all unique additional data field names
      const additionalDataFields = new Set<string>();
      allRecords.forEach(record => {
        if (record.additionalData && record.additionalData.length > 0) {
          record.additionalData.forEach(item => additionalDataFields.add(item.field));
        }
      });
      const additionalDataHeaders = Array.from(additionalDataFields).sort();
      
      // Add additional data headers with 'ADD_' prefix to distinguish them
      headers.push(...additionalDataHeaders.map(field => `ADD_${field}`));
      
            // Create rows
      const rows = allRecords.map(record => {
        // Get owner name using the same logic as in transformedRecords
        let ownerName = '';
        if (record.userId) {
          const userId = String(parseInt(String(record.userId)));
          const user = userMap.get(userId);
          ownerName = user?.name || (record as any).ownerName || userId;
        }
        
        const row = [
          record.id,
          record.brand,
          record.dealer,
          record.groups ? record.groups.join('; ') : '', // Join multiple groups with semicolon
          record.year.toString(),
          record.month.toString(),
          ownerName, // Add owner name to CSV
          record.createdAt || '',
          record.updatedAt || ''
        ];
        
        // Add KPI values
        kpiHeaders.forEach(kpiCode => {
          const kpi = record.kpis?.find(k => k.code === kpiCode);
          row.push(kpi?.value?.toString() || '');
        });
        
        // Add additional data values
        additionalDataHeaders.forEach(field => {
          const additionalItem = record.additionalData?.find(item => item.field === field);
          row.push(additionalItem ? additionalItem.value.toString() : '');
        });
        
        return row;
      });
      
      // Combine headers and rows
      const csvLines = [headers.join(','), ...rows.map(row => row.join(','))];
      return csvLines.join('\n');
    } catch (error) {
      console.error('Error creating CSV content for AI analysis:', error);
      return '';
    }
  };

  // Create validation rules prompt for AI analysis
  const createValidationPrompt = () => {
    if (selectedValidationRules.length === 0) {
      return "Please analyze the KPI data for general insights and patterns.";
    }
    
    // Create the validation rules with weights
    const rulesWithWeights = selectedValidationRules.map(rule => 
      `"${(rule as any).ruletext || (rule as any).ruleText || 'Unknown rule'}" (Weight: ${rule.weight || 1})`
    ).join(', ');
    
    const totalWeight = selectedValidationRules.reduce((sum, rule) => sum + (rule.weight || 1), 0);
    
    // Base prompt - always included
    let prompt = `You are a financial KPI analyst. Analyze the provided KPI data against the following validation rules and create comprehensive financial composite scores with detailed breakdowns by dealer, month, and year.

VALIDATION RULES WITH WEIGHTS:
${rulesWithWeights}

**IMPORTANT DATA STRUCTURE CLARIFICATION:**
The CSV data contains KPI values under column headers that use the FULL KPI NAMES (not codes). For example:
- Column "Total Revenue" contains the values for Total Revenue
- Column "Net Profit" contains the values for Net Profit  
- Column "Return on Assets" contains the values for Return on Assets
When evaluating rules, use the exact KPI values from the appropriately named columns.

CRITICAL SCORING METHODOLOGY - FOLLOW EXACTLY:
You MUST use this exact scoring system for consistent, repeatable results:

**IMPORTANT: SINGLE SCORE ONLY**
- Do NOT show preliminary/incorrect scores followed by corrections
- Do NOT show "original score" and "corrected score"  
- ONLY show the final, accurate score after proper verification
- Verify all rules internally before writing any scores

1. **STARTING SCORE**: Every record starts with exactly 100 points
2. **RULE EVALUATION**: For each validation rule, determine if the record PASSES or FAILS the rule
3. **WEIGHT DEDUCTION**: If a record FAILS a rule, deduct the EXACT weight of that rule from the score
4. **PASS/FAIL ONLY**: There are no partial failures - each rule is either PASS (0 deduction) or FAIL (full weight deduction)
5. **FINAL SCORE**: Final score = 100 - (sum of weights for all failed rules)

SCORING EXAMPLES:
- Record passes all rules: Score = 100
- Record fails rule with weight 15: Score = 100 - 15 = 85
- Record fails rules with weights 10 and 8: Score = 100 - 10 - 8 = 82
- Record fails rules with weights 12, 7, and 5: Score = 100 - 12 - 7 - 5 = 76`;

    // Add structured output format for both modes
    prompt += `

CRITICAL SCORING REQUIREMENT:
- You MUST complete ALL rule evaluations and verifications INTERNALLY FIRST
- Do NOT show any preliminary tables or scores
- ONLY show the final verified table after all calculations are complete
- The table MUST contain only the final, verified, mathematically correct scores
- If you discover errors during verification, you MUST update the table scores to show the CORRECTED values
- NEVER show incorrect scores in the table - always show the final verified scores

REQUIRED OUTPUT FORMAT - EXACT STRUCTURE:

## FINAL DEALER PERFORMANCE SCORES

| Dealer | Year | Month | Score |
|--------|------|-------|-------|`;

    // Add high accuracy mode sections if enabled
    if (highAccuracyMode) {
      prompt += `

**HIGH ACCURACY MODE - STEP-BY-STEP CALCULATION:**

IMPORTANT: Complete ALL calculations internally first, then show ONLY the FINAL DEALER PERFORMANCE SCORES table above, THEN show your detailed working below in these specific markdown sections:

## CALCULATION DETAILS

### DETAILED RULE EVALUATION

For each dealer record, show:

**Record: [Dealer Name] - [Year] - [Month]**
**Starting Score: 100**

**Rule Analysis:**
${selectedValidationRules.map((rule, index) => `
${index + 1}. **Rule**: "${rule.ruleText}" (Weight: ${rule.weight || 1})
   - **Data Value**: [Show the actual KPI value from the data]
   - **Evaluation**: [PASS/FAIL with clear reasoning]
   - **Score Impact**: [If FAIL: -${rule.weight || 1} points, if PASS: No deduction]`).join('')}

**Running Score Calculation:**
- Starting Score: 100
- Total Deductions: [Sum of all failed rule weights]
- **Final Score: [100 - Total Deductions]**

### VERIFICATION CHECK

Now double-check each rule calculation:
${selectedValidationRules.map((rule, index) => `
${index + 1}. **Re-verify Rule**: "${rule.ruleText}"
   - **Data Value Confirmed**: [Re-check the KPI value]
   - **Rule Logic Check**: [Re-confirm PASS/FAIL]
   - **Correction Needed**: [YES/NO - if YES, recalculate that specific rule]`).join('')}

### FINAL VERIFIED CALCULATIONS

**Final Verified Score**: [Show final score after any corrections]

**CRITICAL INSTRUCTIONS:**
- Do NOT show any preliminary tables or scores before the FINAL DEALER PERFORMANCE SCORES table
- Complete all calculations internally before showing any output
- The FINAL DEALER PERFORMANCE SCORES table must ONLY contain the corrected, verified scores
- If you find calculation errors during verification, you MUST go back and update the table with correct scores
- Do NOT leave incorrect scores in the table and only mention corrections in text
- Show EVERY rule for EVERY record in the CALCULATION DETAILS section
- List the exact data value for each KPI referenced in each rule
- Clearly state PASS or FAIL for each rule with reasoning
- Calculate running total deductions step by step
- In verification section, only recalculate rules where errors are found
- The FINAL table scores must exactly match your final verified scores in FINAL VERIFIED CALCULATIONS

**MANDATORY ARITHMETIC VERIFICATION:**
- ALWAYS double-check your basic arithmetic: addition, subtraction
- When calculating total deductions, ADD each failed rule weight one by one
- When calculating final score, use EXACTLY: 100 - (total deductions)
- If you get different results, STOP and recalculate from scratch
- Show your arithmetic step-by-step: "5 + 5 + 4 + 4 + 4 = 22, so 100 - 22 = 78"

**RULE EVALUATION PRECISION:**
- Read each rule condition EXACTLY as written
- Compare the actual data value to the rule threshold PRECISELY
- "below X" means the value must be < X (strictly less than)
- "above X" means the value must be > X (strictly greater than)
- "minimum X" means the value must be >= X (greater than or equal)
- If a value equals the threshold, determine PASS/FAIL based on the exact wording
- NEVER assume a rule fails - verify the exact condition

**WORKFLOW FOR ACCURACY:**
1. Do all calculations internally (not shown to user)
2. For each rule: Extract exact data value → Compare to exact rule condition → Determine PASS/FAIL
3. Add up ONLY the weights of rules that actually FAILED
4. Calculate final score: 100 - (sum of failed rule weights)
5. Double-check arithmetic: does your addition and subtraction equal what you calculated?
6. Show FINAL DEALER PERFORMANCE SCORES table with ONLY the verified correct scores
7. Then show calculation details in hideable sections
8. Never show incorrect scores in the main table`;
    }

    prompt += `

## KEY INSIGHTS & PATTERNS

**Rules Violated:**
[For each failed rule, list: Rule description, Actual data value that caused the failure, Point deduction applied]

**Performance Trends:**
[Identify patterns in dealer performance, seasonal trends, or recurring issues]

**Recommendations:**
[Actionable insights based on the analysis]

CRITICAL REQUIREMENTS:
- Every dealer must have individual monthly/yearly scores in the table
- Use EXACT weight values from the rules - no rounding or approximation
- If a record passes all rules, score = 100
- Scores must be mathematically consistent: 100 - (sum of failed rule weights)
- Do NOT include any visualizations or charts in your response
- Focus on actionable business insights in the Key Insights section
- IMPORTANT: If you make calculation errors, do your verification internally and show ONLY correct scores in the FINAL table
- Never show incorrect scores in the table even if you calculated them initially

**ARITHMETIC ACCURACY REQUIREMENTS:**
- Double-check ALL basic math: addition and subtraction
- For each rule, verify the exact data value against the exact rule condition
- Only count rules that actually FAIL when adding up deductions
- Final score calculation: 100 - (sum of weights for FAILED rules only)
- Example: If rules with weights 5, 5, 4, 4, 4 fail → Total deductions = 22 → Final score = 78

🚨 CRITICAL FINAL STEP - ABSOLUTELY MANDATORY 🚨

## FINAL SUMMARY FOR DISPLAY

⚠️ WARNING: YOU MUST END YOUR RESPONSE WITH THIS EXACT SECTION OR THE USER WILL SEE NOTHING ⚠️

This is the ONLY part that will be displayed to the user. Everything else will be hidden. You MUST include this section or your entire response will be invisible to the user:

### FINAL DEALER PERFORMANCE SCORES

| Dealer | Year | Month | Score |
|--------|------|-------|-------|
[Insert final verified scores here - these must be your corrected, final scores]

### KEY INSIGHTS & PATTERNS

**Rules Violated:**
[For each failed rule, show: Rule text, Actual data value, Expected threshold, Point deduction (e.g., "UNITS_SOLD below 33: Actual value 29, Expected ≥33, Deduction: -5 points")]

**Performance Trends:**
[Key performance patterns identified]

**Recommendations:**
[Top 3 actionable recommendations]

🚨 CRITICAL: You MUST include this "## FINAL SUMMARY FOR DISPLAY" section at the very end of your response. The user interface will ONLY show this section and hide everything else. If you don't include this section, the user will see nothing. 

⚠️ REMINDER: End your response with "## FINAL SUMMARY FOR DISPLAY" section or the user will see a blank screen! ⚠️`;

    return prompt;
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
        <div className="page-header">
          <h1>Run Validation Rules</h1>
        </div>
        
        {error && (
          <div className="error-message">
            Error: {error}
          </div>
        )}
        
        <div className="data-filter-section">
          <FinancialRecordFilterPanel
            initialFilter={filter}
            onApplyFilter={handleApplyFilter}
            onClearFilter={handleClearFilter}
            records={allRecordsForFilters.length > 0 ? allRecordsForFilters : records}
            userMap={userMap}
            filterUserIds={filterUserIds}
            showOwnerFilter={user?.role === 'admin' || user?.role === 'group_admin'}
          />
        </div>
        
        <div className="data-grid-section">
          {isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading KPI records...</p>
            </div>
          ) : records.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📊</div>
              <h3>No KPI Records Found</h3>
              <p>No records match the current filter criteria.</p>
            </div>
          ) : (
            <>
              <div className="data-grid-container">
                <DataGrid 
                  columns={columns}
                  rows={transformedRecords}
                  loading={isLoading}
                  height={600}
                  expandable={true}
                  renderExpandedRow={renderExpandedRow}
                  expandedRowIds={expandedRowIds}
                  onRowExpand={handleRowExpand}
                  onRowCollapse={handleRowCollapse}
                />
              </div>
              
              {totalPages > 1 && (
                <div className="pagination">
                  <div className="pagination-info">
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
                  </div>
                  <div className="pagination-controls">
                    <button 
                      className="pagination-button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span className="pagination-page">{currentPage} of {totalPages}</span>
                    <button 
                      className="pagination-button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Validation Rules Section */}
        <div className="validation-rules-section">
          <h3>Select Validation Rules to Run</h3>
          <p>
            Choose which validation rules you want to apply to your KPI data. The AI will analyze the data against these rules and highlight any violations or areas of concern.
          </p>

          
          {isLoadingRules ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading your validation rules...</p>
            </div>
          ) : rulesError ? (
            <div className="error-message">
              {rulesError}
            </div>
          ) : userValidationRules.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📋</div>
              <h4>No Validation Rules Found</h4>
              <p>You haven't created any validation rules yet. Go to the Validation Rules page to create some rules first.</p>
              <button 
                className="primary-button" 
                onClick={() => window.location.href = '/kpi-maintenance'}
                style={{ marginTop: '10px' }}
              >
                Create Validation Rules
              </button>
            </div>
          ) : (
            <div className="validation-rules-list">
              <h4>Your Validation Rules:</h4>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>Select</th>
                      <th style={{ width: '60px' }}>ID</th>
                      <th style={{ width: '100px' }}>Brand</th>
                      <th>Validation Rule</th>
                      <th>KPIs</th>
                      <th style={{ width: '80px' }}>Weight</th>
                      <th style={{ width: '120px' }}>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userValidationRules.map((rule) => (
                      <tr key={rule.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedValidationRules.some(r => r.id === rule.id)}
                            onChange={() => handleRuleToggle(rule)}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer'
                            }}
                          />
                        </td>
                        <td>{rule.id}</td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            background: 'rgba(0, 200, 255, 0.2)',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {rule.brand || 'N/A'}
                          </span>
                        </td>
                        <td>{rule.ruleText}</td>
                        <td>
                          {(() => {
                            if (!rule.kpiCodes) {
                              return <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>No KPIs</span>;
                            }
                            
                            try {
                              const kpiCodes = JSON.parse(rule.kpiCodes) as string[];
                              
                              if (!kpiCodes || kpiCodes.length === 0) {
                                return <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>No KPIs</span>;
                              }
                              
                              return (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                  {kpiCodes.map((kpiCode, index) => {
                                    const kpiName = getKPIName(kpiCode);
                                    return (
                                      <span
                                        key={index}
                                        style={{
                                          padding: '4px 8px',
                                          background: 'rgba(100, 255, 100, 0.15)',
                                          borderRadius: '4px',
                                          fontSize: '11px',
                                          fontWeight: 'bold',
                                          color: 'rgba(100, 255, 100, 0.9)',
                                          border: '1px solid rgba(100, 255, 100, 0.3)'
                                        }}
                                        title={`KPI Code: ${kpiCode}`}
                                      >
                                        {kpiName}
                                      </span>
                                    );
                                  })}
                                </div>
                              );
                            } catch (error) {
                              return <span style={{ color: 'rgba(255, 100, 100, 0.8)', fontSize: '12px' }}>Invalid KPI data</span>;
                            }
                          })()}
                        </td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            background: 'rgba(255, 200, 0, 0.2)',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: 'rgba(255, 200, 0, 0.9)'
                          }}>
                            {rule.weight || 1}
                          </span>
                        </td>
                        <td>
                          {rule.createdAt ? new Date(rule.createdAt).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {selectedValidationRules.length > 0 && (
                <div className="selected-rules-summary">
                  <p><strong>{selectedValidationRules.length}</strong> validation rule(s) selected for analysis</p>
                </div>
              )}
            </div>
          )}
          
          {selectedValidationRules.length > 0 && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              marginTop: '15px',
              padding: '12px',
              background: 'rgba(0, 50, 100, 0.3)',
              border: '1px solid rgba(0, 200, 255, 0.3)',
              borderRadius: '6px'
            }}>
              <input
                type="checkbox"
                id="highAccuracyMode"
                checked={highAccuracyMode}
                onChange={(e) => setHighAccuracyMode(e.target.checked)}
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: '#00c8ff'
                }}
              />
              <label 
                htmlFor="highAccuracyMode" 
                style={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                High accuracy mode - Show detailed calculation verification and double-checking
              </label>
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
            <CustomAIAnalysisButton 
              prompt={createValidationPrompt()}
              modelId={CLAUDE_45_SONNET_MODEL_ID}
              disabled={totalCount === 0 || selectedValidationRules.length === 0}
              createCSVContent={createCSVContent}
              onAnalysisComplete={setAnalysisResult}
              onOpenReport={openReport}
            />
            {selectedValidationRules.length > 0 && (
              <button 
                onClick={() => setSelectedValidationRules([])}
                className="secondary-button"
              >
                Clear Selection
              </button>
            )}
          </div>
        </div>

        </div>
      </div>

      {/* Report Popup - rendered at root level for proper modal positioning */}
      {isReportOpen && analysisResult && (
        <ReportPopup
          isOpen={isReportOpen}
          onClose={closeReport}
          analysisResult={analysisResult}
          fileName="KPI Analysis Report"
          isKPIAnalysisHighAccuracy={highAccuracyMode}
          kpiCodes={[]}
        />
      )}
    </div>
  );
};

export default KPIAnalyticsPage; 